import angular from 'angular';
import { flow } from 'lodash';
import { format, isMatch, isValid, parse } from 'date-fns';
import currency from 'currency.js';

import { SALES_TYPES, DATE_FORMATS } from '../constants';

import { SALES_IMPORT_ALIGNMENT } from './constants';
import modalSalesUploadConfirmationUrl from './modal-sales-upload-confirmation.html';

export default class UploadSalesCtrl {
  constructor(
    PersonaCollection,
    Utils,
    AlertService,
    Upload,
    Restangular,
    SalesService,
    $window,
    $uibModalInstance,
    $uibModal,
    type
  ) {
    'ngInject';

    Object.assign(this, {
      PersonaCollection,
      Utils,
      AlertService,
      Upload,
      Restangular,
      SalesService,
      $window,
      $uibModalInstance,
      $uibModal,
      type,
      SALES_IMPORT_ALIGNMENT,
    });

    this.uploader = angular.copy(this.Upload.uploader);
    this.uploader.url = `${this.Restangular.configuration.baseUrl}/upload/table`;
    this.uploader.onSuccessItem = (fileItem, response) => {
      try {
        const rows = flow([
          this.transpose.bind(this),
          this.stripEmptyRows.bind(this),
          this.annotateWithTypes.bind(this),
        ])(response);

        if (rows.length < 1) {
          throw 'no rows';
        }
        this.rows = rows;
        this.previewMode = false;
      } catch (e) {
        this.AlertService.danger('Your attachment is invalid.');
        throw e;
      }
    };

    this.uploader.onErrorItem = (item, response, status) => {
      if (status === 413 || status === 0) {
        this.AlertService.danger('Your attachment is too big to upload to Sling.');
        return;
      }

      this.AlertService.danger(
        'Something went wrong while uploading your file. Are you sure it was a valid .xls, .xlsx or .csv file?'
      );
    };

    this.fileAlignment = SALES_IMPORT_ALIGNMENT.HORIZONTAL;
    this.controlKey = this.$window.navigator.appVersion.includes('Mac') ? 'Command ⌘' : 'CTRL';
  }

  get salesTypeLabel() {
    return this.type === SALES_TYPES.actual ? 'actual' : 'projected';
  }

  tryMapLocation(matchedLocationsSet, rowType, _headerCell, options) {
    let type;
    if (rowType === 'number' && _headerCell) {
      const headerCell = String(_headerCell);
      const locationMatches = this.PersonaCollection.locations.filter(
        (loc) => loc.name.toLowerCase().trim() === headerCell.toLowerCase().trim()
      );

      if (locationMatches.length === 1) {
        type = options.find((o) => o.value?.locationId === locationMatches[0].id)?.value;

        if (type) {
          if (matchedLocationsSet.has(type.locationId)) {
            return;
          }

          matchedLocationsSet.add(type.locationId);
        }
      }
    }

    return type;
  }

  annotateWithTypes(rawRows) {
    const matchedLocationsSet = new Set();

    const rows = rawRows.map((row) => {
      const cells = row.map((cell) => this.cellWithType(cell));
      let dataCells = cells;
      let headerCell;
      let firstIsHeader = false;
      let type;

      if (cells[0].type === 'string' && cells.length > 1 && cells[1].type !== 'string') {
        headerCell = cells[0].value;
        dataCells = cells.slice(1);
        firstIsHeader = true;
      }

      const types = dataCells.map((c) => c.type);
      const rowType = types.every((t) => t === types[0]) ? types[0] : undefined;
      const options = [
        {
          label: 'Date',
          submenu: this.getAllDateOptions(),
        },
        ...this.PersonaCollection.locations.map((loc) => ({
          label: loc.name,
          value: {
            locationId: loc.id,
          },
        })),
      ];

      type = this.tryMapLocation(matchedLocationsSet, rowType, headerCell, options);

      if (rowType === 'date') {
        let formats = Object.entries(
          dataCells
            .reduce((acc, c) => acc.concat(c.formats), [])
            .reduce((acc, f) => ((acc[f] = (acc[f] || 0) + 1), acc), {})
        );

        formats = formats
          .filter(([, count]) => count === dataCells.length)
          .map(([format]) => format);

        if (formats.length === 1) {
          type = options[0].submenu.find((o) => o.value?.date === formats[0])?.value;
        }
      }

      return {
        cells,
        options,
        firstIsHeader,
        type,
      };
    });

    this.firstIsHeader = rows.every((row) => row.firstIsHeader);

    return rows;
  }

  cellWithType(cell) {
    const date = this.tryDate(cell);
    if (date) {
      return date;
    }

    if (cell !== '') {
      const curr = currency(cell);
      if (curr.value) {
        return {
          type: 'number',
          value: curr.value,
          displayValue: curr.format(),
        };
      }
    }

    return {
      type: 'string',
      value: String(cell),
      displayValue: String(cell),
    };
  }

  getAllDateOptions() {
    return Object.entries(DATE_FORMATS).map(([format, example]) => ({
      label: example,
      toggleLabel: 'Date',
      value: {
        date: format,
      },
    }));
  }

  tryDate(val) {
    const validFormats = Object.keys(DATE_FORMATS).filter((format) =>
      this.isDateValid(val, format)
    );

    if (validFormats.length) {
      return {
        type: 'date',
        formats: validFormats,
        value: val,
        displayValue: val,
      };
    }
  }

  // @INFO it requires extra checking against two digit year
  // more info: https://github.com/date-fns/date-fns/issues/942
  isDateValid(date, dateFormat) {
    const dateString = String(date);
    const parsedData = parse(dateString, dateFormat, new Date());
    if (!isValid(parsedData)) {
      return false;
    }

    return isMatch(dateString, dateFormat) && format(parsedData, dateFormat) === dateString;
  }

  next() {
    this.showColumnsSelectionValidations = true;

    if (!this.previewMode) {
      if (!this.tableError) {
        this.previewMode = true;
        return true;
      }
    } else {
      return this.submit();
    }
  }

  back() {
    if (this.previewMode) {
      this.previewMode = false;
    } else {
      delete this.rows;
    }
  }

  transpose(rows) {
    if (this.fileAlignment === SALES_IMPORT_ALIGNMENT.HORIZONTAL) {
      return rows;
    }

    return this.Utils.transpose2dTable(rows);
  }

  stripEmptyRows(rows) {
    return rows.filter((row) => !row.every((cell) => !cell));
  }

  extractSales() {
    let datesIndex;
    const data = this.rows
      .filter((r) => r.type)
      .map(({ type, cells: allCells }, index) => {
        const cells = allCells.filter((c, i) => this.selectedColumns[i]);
        if (type.date) {
          datesIndex = index;
          return cells.map((c) =>
            this.isDateValid(c.value, type.date)
              ? format(parse(c.value, type.date, new Date()), 'yyyy-MM-dd')
              : null
          );
        }

        if (type.locationId) {
          return cells.map((c) => ({
            ...type,
            locationName: this.PersonaCollection.personasDict[type.locationId]?.name,
            amount: c.type === 'number' ? c.value : null,
          }));
        }

        return null;
      });

    const [dates] = data.splice(datesIndex, 1);

    data.forEach((item) => {
      item.forEach((sale, index) => {
        sale.date = dates[index];
      });
    });

    return data
      .reduce((acc, arr) => acc.concat(arr), [])
      .filter((sale) => sale.date !== null && sale.amount !== null);
  }

  get rowName() {
    return this.fileAlignment === SALES_IMPORT_ALIGNMENT.HORIZONTAL ? 'row' : 'column';
  }

  get colName() {
    return this.fileAlignment === SALES_IMPORT_ALIGNMENT.HORIZONTAL ? 'column' : 'row';
  }

  async submit() {
    let payload = this.extractSales();
    payload = this._manageSaleTypes(payload);
    await this.SalesService.importSales(payload);

    const [importedFrom, importedTo, importedLocationNames] = this.getImportSummary(payload);
    const importedLocationNamesString = this.Utils.addJoin(importedLocationNames);

    const typeLabel = this.salesTypeLabel;
    this.$uibModalInstance.close();
    this.$uibModal
      .open({
        size: '620',
        templateUrl: modalSalesUploadConfirmationUrl,
        controller() {
          this.importedFrom = importedFrom;
          this.importedTo = importedTo;
          this.importedLocationNames = importedLocationNames;
          this.importedLocationNamesString = importedLocationNamesString;
          this.typeLabel = typeLabel;
        },
        controllerAs: 'ctrl',
      })
      .result.ignoreCancel();
  }

  _manageSaleTypes(payload) {
    if (this.type === SALES_TYPES.override) {
      return payload;
    }

    return payload.map((item) => {
      item.type = this.type;
      return item;
    });
  }

  getImportSummary(importedSalesList) {
    const locationNames = [];
    let dateFrom;
    let dateTo;
    importedSalesList.forEach((sale) => {
      const date = this.Utils.getRealDate(sale.date);
      if (!dateFrom || date < dateFrom) {
        dateFrom = date;
      }

      if (!dateTo || date > dateTo) {
        dateTo = date;
      }

      locationNames.push(sale.locationName);
    });

    return [dateFrom, dateTo, this.Utils.uniq(locationNames)];
  }
}
